import React from "react";
import { useHistory, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import CircleCheckMark from "./icons/CircleCheckMark";
import ActionButton from "./shared/button/ActionButton";
import Auth from "../services/Auth";
import Loading from "./Loading";
import Localization from "../services/Localization";
import * as Components from "../resources/Components";

const ChangePasswordSuccess = () => {
  const history = useHistory();

  const storefrontNumber = useSelector(
    (state) => state.user.currentUser?.storefrontNumber
  );
  const locale = useSelector(
    (state) => state.user.currentUser?.locale || "en_US"
  );

  const {
    data: labels = {},
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "changePasswordLabels",
      Components.CHANGE_PASSWORD,
      locale,
      storefrontNumber,
    ],
    queryFn: async () => {
      try {
        const labels = await Localization.getComponentLabels(
          Components.CHANGE_PASSWORD,
          locale,
          storefrontNumber
        );

        return labels || {};
      } catch (error) {
        console.error("Error fetching Change Password labels:", error);
        return {};
      }
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  const handleGoToDashboard = () => {
    history.push("/");
  };

  const handleLogout = () => {
    Auth.signOut("/login");
  };

  if (isLoading) {
    return <Loading type="brand" />;
  }

  if (error) {
    console.error("Error loading change password labels:", error);
  }

  return (
    <div
      className="fele-message-standard"
      style={{
        display: "flex",
        justifyContent: "center",
        padding: "5rem",
        marginBottom: "5rem",
      }}
    >
      <div
        className="text-center"
        style={{
          maxWidth: "600px",
          border: "1px solid #E9ECEF",
          borderRadius: "8px",
          padding: "3rem",
        }}
      >
        {/* Success Icon */}
        <div style={{ marginBottom: "3rem" }}>
          <span aria-hidden="true">
            <CircleCheckMark width={32} height={32} />
          </span>
        </div>

        {/* Success Message */}
        <h1
          className="heading1"
          style={{
            marginBottom: "1.5rem",
            color: "#3C763D",
            fontSize: "3.2rem",
            fontWeight: "700",
          }}
        >
          {labels.PasswordUpdatedSuccessfully ||
            "Password Updated Successfully"}
        </h1>

        <p
          className="heading2"
          style={{
            marginBottom: "2.5rem",
            color: "#666",
            fontSize: "2rem",
            lineHeight: "1.4",
          }}
        >
          {labels.PasswordChangedSuccessfully ||
            "Your password has been changed successfully. You can now use your new password to sign in to your account."}
        </p>

        {/* Security Note */}
        <div
          style={{
            backgroundColor: "#F8F9FA",
            border: "1px solid #E9ECEF",
            borderRadius: "8px",
            padding: "1.5rem",
            marginBottom: "3rem",
            textAlign: "left",
          }}
        >
          <div
            style={{
              fontSize: "1.6rem",
              fontWeight: "600",
              color: "#495057",
              marginBottom: "0.5rem",
            }}
          >
            🔒 {labels.SecurityReminder || "Security Reminder"}
          </div>

          <div
            style={{
              fontSize: "1.5rem",
              color: "#6C757D",
              lineHeight: "1.5",
            }}
          >
            {labels.SecurityReminderText ||
              "For your security, please remember to:"}
            <ul
              style={{
                marginTop: "0.5rem",
                marginBottom: "0",
                paddingLeft: "2rem",
              }}
            >
              <li>
                {labels.KeepPasswordConfidential ||
                  "Keep your password confidential"}
              </li>

              <li>
                {labels.UseStrongPassword || "Use a strong, unique password"}
              </li>

              <li>
                {labels.SignOutSharedDevices ||
                  "Sign out when using shared devices"}
              </li>
            </ul>
          </div>
        </div>

        {/* Action Buttons */}
        <div
          style={{
            display: "flex",
            gap: "1.5rem",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <ActionButton
            text={labels.GoToDashboard || "Go to Dashboard"}
            variant="blue-solid"
            onClick={handleGoToDashboard}
            dataCy="go-to-dashboard"
            style={{ flex: "1 1 100%", minWidth: "176.84px" }}
          />

          <ActionButton
            text={labels.Logout || "Logout"}
            variant="blue-outline"
            onClick={handleLogout}
            dataCy="logout"
            style={{ flex: "1 1 100%", minWidth: "176.84px" }}
          />
        </div>

        {/* Additional Info */}
        <div
          style={{
            marginTop: "3rem",
            fontSize: "1.5rem",
            color: "#6C757D",
          }}
        >
          {labels.NeedHelpText ||
            "Need help? Contact our support team or check our"}{" "}
          <Link
            to="/help"
            style={{
              color: "#118ABD",
              textDecoration: "none",
              fontWeight: "600",
            }}
            onMouseEnter={(e) => (e.target.style.textDecoration = "underline")}
            onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
          >
            {labels.SupportPage || "support page"}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordSuccess;
